import React from 'react';
import styled from 'styled-components';
import Section from '../components/Section';
import Container from '../components/Container';
import Button from '../components/Button';

// Hero Section
const Hero = styled.div`
  background: url('https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1920&q=80');
  background-size: cover;
  background-position: center;
  color: ${({ theme }) => theme.colors.light};
  height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(40,126,60,0.4) 0%, rgba(0,82,156,0.4) 100%);
    z-index: 1;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 10vh;
    background: linear-gradient(to top, ${({ theme }) => theme.colors.light}, transparent);
    z-index: 1;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 50vh;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  padding: 0 ${({ theme }) => theme.spacing.md};
  z-index: 2;
  position: relative;
  
  h1 {
    font-size: ${({ theme }) => theme.fontSizes.huge};
    margin-bottom: ${({ theme }) => theme.spacing.md};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    text-shadow: 0 2px 8px rgba(0,0,0,0.3);
    color: white;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.fontSizes.xxxl};
    }
  }
  
  p {
    font-size: ${({ theme }) => theme.fontSizes.lg};
    max-width: 600px;
    margin: 0 auto ${({ theme }) => theme.spacing.lg};
    line-height: 1.6;
    text-shadow: 0 2px 4px rgba(0,0,0,0.3);
    color: white;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.fontSizes.md};
    }
  }
`;

// Donation Information Section
const InfoSection = styled(Section)`
  background-color: ${({ theme }) => theme.colors.light};
  padding: ${({ theme }) => theme.spacing.xxxl} 0;
  text-align: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.xxl} 0;
  }
`;

const InfoContainer = styled(Container)`
  max-width: 1000px;
`;

const InfoText = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
  
  h2 {
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    font-size: ${({ theme }) => theme.fontSizes.xxl};
    position: relative;
    display: inline-block;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 3px;
      background-color: ${({ theme }) => theme.colors.accent};
    }
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.fontSizes.xl};
    }
  }
  
  p {
    font-size: ${({ theme }) => theme.fontSizes.md};
    line-height: 1.6;
    max-width: 800px;
    margin: 0 auto;
  }
`;

// Donation Methods Section
const MethodsSection = styled(Section)`
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.xxxl} 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.xxl} 0;
  }
`;

const MethodsContainer = styled(Container)`
  max-width: 1200px;
`;

const MethodsHeading = styled.h2`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.xl};
    margin-bottom: ${({ theme }) => theme.spacing.xl};
  }
`;

const MethodsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const MethodCard = styled.div`
  background: ${({ theme }) => theme.colors.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: ${({ theme }) => theme.spacing.xl};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-height: 400px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
  
  h3 {
    color: ${({ theme }) => theme.colors.secondary};
    margin: ${({ theme }) => theme.spacing.md} 0;
    font-size: ${({ theme }) => theme.fontSizes.xl};
  }
  
  p {
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    line-height: 1.6;
    flex-grow: 1;
  }
  
  img {
    max-width: 150px;
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: 350px;
  }
`;

const QRCode = styled.img`
  max-width: 200px !important;
  margin: ${({ theme }) => theme.spacing.md} 0;
`;

// Donation Policies Section
const PoliciesSection = styled(Section)`
  background-color: ${({ theme }) => theme.colors.light};
  padding: ${({ theme }) => theme.spacing.xxxl} 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.xxl} 0;
  }
`;

const PoliciesContainer = styled(Container)`
  max-width: 1200px;
`;

const PoliciesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const PolicyCard = styled.div`
  background: ${({ theme }) => theme.colors.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
  }
  
  .icon {
    font-size: 2.5rem;
    color: ${({ theme }) => theme.colors.accent};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
  
  h3 {
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: ${({ theme }) => theme.spacing.md};
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
  
  p {
    font-size: ${({ theme }) => theme.fontSizes.md};
    line-height: 1.6;
  }
`;

// CTA Section
const CTASection = styled(Section)`
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  color: ${({ theme }) => theme.colors.light};
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  text-align: center;
`;

const CTAContent = styled.div`
  max-width: 700px;
  margin: 0 auto;
  
  h2 {
    margin-bottom: ${({ theme }) => theme.spacing.lg};
    font-size: ${({ theme }) => theme.fontSizes.xxl};
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.fontSizes.xl};
    }
  }
  
  p {
    margin-bottom: ${({ theme }) => theme.spacing.xl};
    font-size: ${({ theme }) => theme.fontSizes.lg};
    line-height: 1.6;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.fontSizes.md};
    }
  }
`;

const Donate: React.FC = () => {
  return (
    <>
      <Hero>
        <HeroContent>
          <h1>Make a Difference Today</h1>
          <p>Your generous support empowers families toward self-sufficiency and creates lasting change in our community.</p>
        </HeroContent>
      </Hero>

      <InfoSection>
        <InfoContainer>
          <InfoText>
            <h2>Every Contribution Makes a Difference</h2>
            <p>
              Your support, no matter the amount, goes a long way in making an impact on families across the Bay Area.
              When you donate to Project Assist, you're helping families recover from hardship, find stability,
              and build pathways to self-sufficiency.
            </p>
            <p>
              We are currently working with a few individuals that require immediate assistance, you can help them on being self sufficient by donating to Project Assist.
            </p>
          </InfoText>
        </InfoContainer>
      </InfoSection>

      <MethodsSection>
        <MethodsContainer>
          <MethodsHeading>Ways to Donate</MethodsHeading>
          <MethodsGrid>
            <MethodCard>
              <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg" alt="PayPal" style={{ height: "40px", marginBottom: "20px" }} />
              <h3>Donate via PayPal</h3>
              <p>
                Make a secure donation through PayPal. You can use your PayPal account or credit card for a one-time donation.
              </p>
              <a
                href="https://www.paypal.com/donate/?hosted_button_id=GT7Z7G6Q4KWDA"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="accent">
                  Donate with PayPal
                </Button>
              </a>
            </MethodCard>

            <MethodCard>
              <img src="https://upload.wikimedia.org/wikipedia/commons/4/4c/Zelle_logo.svg" alt="Zelle" style={{ height: "40px", marginBottom: "20px" }} />
              <h3>Donate via Zelle</h3>
              <p>
                Send your donation directly through Zelle using the recipient information below, or scan the QR code with your banking app.
              </p>
              <QRCode src="https://enroll.zellepay.com/qr-codes?data=eyJuYW1lIjoiUFJPSkVDVCBBU1NJU1QiLCJhY3Rpb24iOiJwYXltZW50IiwidG9rZW4iOiJhZG1pbkBwcm9qZWN0LWFzc2lzdC5uZXQifQ==" alt="Zelle QR Code" />
              <p><strong>Recipient Name:</strong> Project Assist<br />
                <strong>Recipient Zelle ID:</strong> admin@project-assist.net</p>
              <a
                href="https://enroll.zellepay.com/qr-codes?data=eyJuYW1lIjoiUFJPSkVDVCBBU1NJU1QiLCJhY3Rpb24iOiJwYXltZW50IiwidG9rZW4iOiJhZG1pbkBwcm9qZWN0LWFzc2lzdC5uZXQifQ=="
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button>
                  Open Zelle
                </Button>
              </a>
            </MethodCard>
          </MethodsGrid>
        </MethodsContainer>
      </MethodsSection>

      <PoliciesSection>
        <PoliciesContainer>
          <PoliciesGrid>
            <PolicyCard>
              <div className="icon">
                <i className="fas fa-moon-and-star"></i>
                {/* Fallback if FontAwesome isn't available */}
                ☪️
              </div>
              <h3>Zakat Eligibility</h3>
              <p>
                We accept both Sadaqa and Zakat. For us to properly allocate your donations,
                please mention "Zakat" in notes. All other donations with no zakat note will be
                considered a Sadaqa/General Donation.
              </p>
            </PolicyCard>

            <PolicyCard>
              <div className="icon">
                <i className="fas fa-file-certificate"></i>
                {/* Fallback if FontAwesome isn't available */}
                📜
              </div>
              <h3>Tax Deductibility</h3>
              <p>
                We are a 501c3 designated organization so your donations qualify for tax deductions.
                A receipt will be issued for your records. Project Assist EIN: 88-1941937
              </p>
            </PolicyCard>

            <PolicyCard>
              <div className="icon">
                <i className="fas fa-handshake"></i>
                {/* Fallback if FontAwesome isn't available */}
                🤝
              </div>
              <h3>Company Matching</h3>
              <p>
                Project Assist is an approved organization and registered in the Benevity's Causes Portal.
                Your company match for the donation is applicable.
              </p>
            </PolicyCard>
          </PoliciesGrid>
        </PoliciesContainer>
      </PoliciesSection>

      <CTASection>
        <Container>
          <CTAContent>
            <h2>Join Us in Creating Lasting Change</h2>
            <p>
              Your support creates pathways to self-sufficiency for vulnerable families in our community.
              Together, we can make a meaningful difference.
            </p>
            <a
              href="https://www.paypal.com/donate/?hosted_button_id=GT7Z7G6Q4KWDA"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="accent" size="large">
                Donate Now
              </Button>
            </a>
          </CTAContent>
        </Container>
      </CTASection>
    </>
  );
};

export default Donate; 